import type { InputNumberProps, InputNumberState, IntegerMarksType } from './iInputNumber'
import Message from '../../wui-message/src'
import { matchAll, formatUtils } from '../../wui-core/src/index'
import { globalConfig } from '../../wui-provider/src'
import i18n from './i18n.js'
import { getLangInfo } from '../../wui-locale/src/tool'

/**
 * 轻量级精度计算工具，替代 decimal.js
 * 主要用于解决 JavaScript 浮点数精度问题
 */

/**
 * 获取小数位数
 */
function getDecimalLength(num: number | string): number {
    const str = String(num);
    const decimal = str.split('.')[1];
    return decimal ? decimal.length : 0;
}

/**
 * 将数字转换为整数（通过放大）
 */
function toInteger(num: number | string, scale: number): number {
    const str = String(num);
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) {
        return Number(str + '0'.repeat(scale));
    }
    const intPart = str.slice(0, dotIndex);
    const decimalPart = str.slice(dotIndex + 1);
    const paddedDecimal = decimalPart + '0'.repeat(scale - decimalPart.length);
    return Number(intPart + paddedDecimal.slice(0, scale));
}

/**
 * 精确加法
 */
export function plus(num1: number | string, num2: number | string): number {
    const scale = Math.max(getDecimalLength(num1), getDecimalLength(num2));
    if (scale === 0) {
        return Number(num1) + Number(num2);
    }
    const factor = Math.pow(10, scale);
    const int1 = toInteger(num1, scale);
    const int2 = toInteger(num2, scale);
    return (int1 + int2) / factor;
}

/**
 * 精确减法
 */
export function minus(num1: number | string, num2: number | string): number {
    const scale = Math.max(getDecimalLength(num1), getDecimalLength(num2));
    if (scale === 0) {
        return Number(num1) - Number(num2);
    }
    const factor = Math.pow(10, scale);
    const int1 = toInteger(num1, scale);
    const int2 = toInteger(num2, scale);
    return (int1 - int2) / factor;
}

/**
 * 比较大小
 */
export function greaterThan(num1: number | string, num2: number | string): boolean {
    return Number(num1) > Number(num2);
}

export function lessThan(num1: number | string, num2: number | string): boolean {
    return Number(num1) < Number(num2);
}

/**
 * 格式化为固定小数位
 */
export function toFixed(num: number | string, precision: number): string {
    return Number(num).toFixed(precision);
}

/**
 * Decimal 兼容类（提供与 decimal.js 相似的 API）
 */
export class PrecisionNumber {
    private value: number;

    constructor(value: number | string) {
        this.value = Number(value);
    }

    plus(other: number | string | PrecisionNumber): PrecisionNumber {
        const otherValue = other instanceof PrecisionNumber ? other.value : Number(other);
        return new PrecisionNumber(plus(this.value, otherValue));
    }

    minus(other: number | string | PrecisionNumber): PrecisionNumber {
        const otherValue = other instanceof PrecisionNumber ? other.value : Number(other);
        return new PrecisionNumber(minus(this.value, otherValue));
    }

    greaterThan(other: number | string | PrecisionNumber): boolean {
        const otherValue = other instanceof PrecisionNumber ? other.value : Number(other);
        return greaterThan(this.value, otherValue);
    }

    lessThan(other: number | string | PrecisionNumber): boolean {
        const otherValue = other instanceof PrecisionNumber ? other.value : Number(other);
        return lessThan(this.value, otherValue);
    }

    toFixed(precision: number): string {
        return toFixed(this.value, precision);
    }

    toString(): string {
        return String(this.value);
    }

    valueOf(): number {
        return this.value;
    }
}

export const defaultIntegerMarks = [
    {
        len: 3,
        // mark: '百',
        key: 'hundred'
    },
    {
        len: 4,
        // mark: '千',
        key: 'thousand'
    },
    {
        len: 5,
        // mark: '万',
        key: 'tenThousand'
    },
    {
        len: 6,
        // mark: '十万',
        key: 'hundredThousand'
    },
    {
        len: 7,
        // mark: '百万',
        key: 'million'
    },
    {
        len: 8,
        // mark: '千万',
        key: 'tenMillion'
    },
    {
        len: 9,
        // mark: '亿',
        key: 'hundredMillion'
    },
    {
        len: 10,
        // mark: '十亿',
        key: 'billion'
    },
    {
        len: 11,
        // mark: '百亿',
        key: 'tenBillion'
    },
    {
        len: 12,
        // mark: '千亿',
        key: 'hundredBillion'
    },
    {
        len: 13,
        // mark: '万亿',
        key: 'trillion'
    },
    {
        len: 14,
        // mark: '十万亿',
        key: 'tenTrillion'
    },
    {
        len: 15,
        // mark: '百万亿',
        key: 'hundredTrillion'
    },
    {
        len: 16,
        // mark: '千万亿',
        key: 'quadrillion'
    },
]

export function prompt(content?: string) {
    Message.destroy()
    Message.create({content, color: 'warninglight'})
}

export function setCaretPosition(ctrl: HTMLInputElement, pos: number, need: boolean) {
    if (ctrl && need) {
        if (ctrl.setSelectionRange && ctrl.type !== 'number') {
            ctrl.focus()
            ctrl.setSelectionRange(pos, pos)
            // IE8 and below，兼容IE场景，单测不覆盖
        } else if ((ctrl as any).createTextRange) {
            let range = (ctrl as any).createTextRange()
            range.collapse(true)
            range.moveEnd('character', pos)
            range.moveStart('character', pos)
            range.select()
        }
    }
}

/**
 * 分割字符串
 * 示例：'-123,45.06789'  ===>  ['-', '123', ',', '45', '.', '06789']
 */
export function splitValue(value: string | number): string[] {
    value = value || value === 0 ? String(value) : '';
    const reg = /(\D)?(\d+)?(\D)?/g; // 注意用户最开始仅输入负号-或小数点时场景，所以数字不是必须的
    const res: string[] = [];
    for (let item of matchAll(value, reg)) {
        item.map((str, i) => i > 0 && !!str && res.push(str));
    }
    return res;
}

/**
 * 移除分隔符，输出可计算数字字符串
 * 示例：'-123,45.06789'  ===>  '-12345.06789'
 */
export function value4calc({
    value,
    thousandSeparator = ',',
    decimalSeparator = '.'
}: {
    value?: string | number;
    thousandSeparator?: string;
    decimalSeparator?: string;
}): string {
    if (typeof value === 'number') return String(value);
    if (value === null || value === undefined) return '';

    let _value = splitValue(value);
    value = _value
        .map(item => {
            if (item === thousandSeparator) {
                item = '';
            } else if (item === decimalSeparator) {
                item = '.';
            } else if (item && !/[0-9e+-]+/ig.test(item)) { // 删除其他非法字符
                item = ''
            }
            return item;
        })
        .join('');
    return value;
}

/**
* 转化为金额大写
*/
export function convertToChineseCurrency(val: string | number) {
    let value = Number(val);

    if (typeof val === 'string' && /^[^-]*-$/.test(val)) return val[val.length - 1] + val.slice(0, val.length - 1);
    if (val === '' || val == null) return '';
    //  0显示为零元整
    if (value === 0) return '零元整';
    // 负数显示原数值
    if (value < 0) return val;
    if ((typeof val === 'string' && val.includes("e")) || Number.isNaN(value)) return '错误的金额！'

    // 15判断是因为如果有16位整数+两位小数，Number会丢失小数精度
    let [integer, decimal = '0'] = typeof val === 'string' && val.length > 15 ? val.split('.') : value.toString().split('.');

    integer = integer.replace(/^0+/, ''); // 删除所有开头的零
    let numArr = ['', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    let descArr = ['', '拾', '佰', '仟', '万', '拾', '佰', '仟', '亿', '拾', '佰', '仟', '万亿', '拾', '佰', '仟', '兆', '拾', '佰', '仟'];
    let rmbDescArr = ['角', '分'];

    // 整数部分的读法
    let integerRes = '';
    let count = integer.length; // 整数位数
    if (count > 16 || (count > String(Number.MAX_SAFE_INTEGER).length)) {
        return '错误的金额！';
    } else if (count === 0) {
        integerRes = '零';
    } else {
        for (let i = 0; i < count; i++) {
            let n = integer[i]; // 当前位上的数字
            // 当前数字的单位索引 descArr
            let j = count - i - 1;

            // 判断是否需要在数字前加“零”
            let isLing = i > 1 && n !== '0' && integer[i - 1] === '0'; // 去除首位且本位不是零上一位是零
            let cnZero = isLing ? '零' : '';
            // 获取当前数字的中文大写
            let cnNum = numArr[n as unknown as number]; // 类型断言，避免ts变严的类型审查导致标红

            // 判断当前单位是否需要省略
            let isEmptyUnit = (n === '0' && j % 4 !== 0) || integer.slice(i - 3, i + 1) === '0000';
            let cnDesc = isEmptyUnit ? '' : descArr[j];
            integerRes += cnZero + cnNum + cnDesc;
        }
    }

    // 小数部分的读法
    let decimalRes = '';
    count = (decimal || '').length;

    if (decimal === null) {
        decimalRes = '整';
    } else if (decimal === '0') {
        decimalRes = '';
    } else if (count > 2) {
        return '错误的金额！' // 规范，只处理两位小数情况
    } else {
        for (let i = 0; i < count; i++) {
            let n = decimal[i];
            // 判断是否需要在数字前加“零”
            let isLing = i > 0 && n !== '0' && decimal[i - 1] === '0';
            let cnZero = isLing && integerRes !== '零' ? '零' : '';
            let cnNum = numArr[n as unknown as number]; // 类型断言，避免ts变严的类型审查导致标红
            let cnDesc = cnNum ? rmbDescArr[i] : '';

            decimalRes += cnZero + cnNum + cnDesc;
        }
    }

    let res = integerRes === '零' ? decimalRes : integerRes + (decimalRes === '' ? '元整' : `元${decimalRes}`);

    return res
}

/**
*  获取格式配置，包括：千分位、小数点、负号位置、是否需要千分位;
* @returns {thousandSeparator, decimalSeparator, minusRight, toThousands}
*/
export function getFormat(props: InputNumberProps) {
    const {thousandSeparator, decimalSeparator, minusRight, toThousands} = props;
    // format接入工作台首选项等provider配置
    const globalDataFormat = globalConfig().getGlobalDataFormat();
    const {numberFormat} = globalDataFormat;
    if (numberFormat) {
        let diworkFormat = formatUtils.diworkfFormat2Num(numberFormat);
        return {
            thousandSeparator: toThousands === false ? '' : (thousandSeparator ?? diworkFormat.thousandSeparator),
            decimalSeparator: decimalSeparator ?? diworkFormat.decimalSeparator,
            minusRight: minusRight ?? diworkFormat.minusRight,
            toThousands: toThousands ?? !!diworkFormat.thousandSeparator,
        }
    } else {
        return {
            thousandSeparator: toThousands === false ? '' : (thousandSeparator ?? ','),
            decimalSeparator: decimalSeparator ?? '.',
            minusRight: minusRight ?? false,
            toThousands: toThousands ?? false,
        }
    }
}

/**
*  @memberof InputNumber
* round 是否要四舍五入(此参数无效,超长不让输入)
*/
export function numToFixed(decimalSeparator: any, value: string | number, precision?: InputNumberProps['precision'], round?: boolean): string {
    value = String(value)
    if (!value && value !== '0') return value
    if (!precision && String(precision) !== '0') return value
    let preIndex = value.indexOf(decimalSeparator)
    if (value.indexOf(decimalSeparator) === -1) return value
    preIndex++
    let endIndex = preIndex + (precision ?? 0)
    let precValue = value.substring(preIndex, preIndex + endIndex) + '0000000000'
    if (round) {
        value = value.replace(decimalSeparator, '.') // 用户正常数字输入，首选项配置格式转换为小数点
        return (Number(value) + 1e-14).toFixed(precision)
    }
    let result = value.split(decimalSeparator)[0] + decimalSeparator + precValue.substring(0, precision);
    if (value[value.length - 1] === '-' && result[result.length - 1] !== '-') result = result + '-'
    return result
}

/**
* roundAt 自定义舍入规则
*/
export function customRound(num: any, digits: number, roundUpOn: number) {
    if (!digits && digits !== 0) return num
    let roundUpOnFinal = roundUpOn === 10 ? 1 : roundUpOn / 10;
    // 计算放大系数
    let factor = Math.pow(10, digits);
    // 放大数值到指定的小数位
    let value = num * factor;

    // 获取小数点后的数值
    let decimalPart = value && value.toString()?.split('.').length > 1 ? Number('0.' + value.toString()?.split('.')[1][0]) : 0;

    // 自定义规则：如果 decimalPart 大于等于 roundUpOn，就进位
    if (decimalPart >= roundUpOnFinal) {
        value = Math.ceil(value); // 向上取整
    } else {
        value = Math.floor(value); // 向下取整
    }

    let result = (value / factor).toFixed(digits); // 除以放大系数并返回固定小数位
    return result;
}

/**
*  判断newState对比state是否有不同的值, 返回true表示有改变
*/
export function stateHasDiff(newState: { [key: string]: any }, state: { [key: string]: any }) {
    return !Object.entries(newState).every(([key, value]) => value === state[key]);
}

/**
* 千分符
* @param {number} number 要转换的数据，可计算数字字符串
* @param {boolean} isProps 是否是用户传入值，传入值按无格式数值字符串处理，即认为无千分位分隔符且小数点为 点 `.`
*/
export function value4show(number: number | string, thousandSeparator: string, decimalSeparator: string, minusRight: boolean = false): string {
    if (number === '') return ''
    if (number === '0') return '0'
    let num = (number || 0).toString()
    num = '' + changeMinusRightPosition(num, minusRight)
    if (!isNaN(+num)) {
        num = num.replace('.', decimalSeparator) // 用户传入的正常数字'22.333'区别于千分位`.`格式化出来的数据'22.333'，小数点转换为与首选项配置格式
    }
    let [integer, decimal = ''] = num.split(decimalSeparator)
    const hasMinus = integer.includes('-')
    integer = integer.replace('-', '')
    let result = ''
    while (integer.length > 3) {
        result = thousandSeparator + integer.slice(-3) + result
        integer = integer.slice(0, integer.length - 3)
    }
    if (integer) {
        result = integer + result
        if (num == decimalSeparator || num.indexOf(decimalSeparator) == num.length - 1) {
            result = result + decimalSeparator + decimal
        } else if (decimal) {
            result = result + decimalSeparator + decimal
        }
    }
    if (hasMinus) {
        result = minusRight ? result + '-' : '-' + result
    }
    return result
}

/**
* 删除输入的空格
* @param {Number|String} value 输入数字
*/
export function deleteSpaces(value: string | number) {
    return typeof value === 'string' ? value.replace(/\s/g, '') : value.toString();
}

/**
* 恢复科学技术法的问题
*/
export function getFullNum({ roundAt = 5, value: num, decimalSeparator, precision }: { roundAt: number, value: string | number, decimalSeparator: string, precision?: number }): string {
    // 处理非数字
    if (isNaN(+num)) {
        return num + ''
    }

    // 处理不需要转换的数字
    const str = '' + num
    if (!/e/i.test(str)) {
        return str
    }
    const reg = new RegExp(`${decimalSeparator === '.' ? '\\.' : decimalSeparator}?0+$`)
    let result = roundAt === 5 ? Number(num).toFixed(precision ?? 18) : customRound(num, precision ?? 18, roundAt)
    return result.replace(reg, '')
}

/**
* @desc 遮掩码占位码
*/
export function decodeValue(value: any, formatReg: any, hiddenChart = '*', replaceChart = '#') {
    if (!formatReg) {
        return value
    }
    const formatRegArr = formatReg?.split('')
    const valueArr = String(value)?.split('')
    let newValue = []
    for (let index = 0; index < formatRegArr.length; index++) {
        const element = formatRegArr[index]
        if (element === replaceChart) {
            const cell = valueArr?.shift()
            if (!cell) break
            newValue.push(cell)
        } else if (element === hiddenChart) {
            const cell = valueArr?.shift()
            if (!cell) break
            newValue.push(hiddenChart)
        } else {
            newValue.push(element)
        }
    }
    if (valueArr.length > 0) {
        newValue = newValue.concat(valueArr)
    }
    return newValue.join('')
}

/**
* 去掉千分位
*/
export function delcommafy({num, thousandSeparator}: {num: string, thousandSeparator: string}) {
    // 去除千分位中的‘，’
    if (num && num != 'undefined' && num != 'null') {
        let numS = num
        numS = numS.toString()
        let reg = new RegExp(thousandSeparator === '.' ? '\\.' : thousandSeparator, 'ig')
        numS = numS.replace(reg, '')
        return numS
    } else {
        return num
    }
}

/**
* 分离小数和整数，返回小数或整数部分
*/
export function separate(decimalSeparator: string, thousandSeparator: string, value: string | number, isInt: boolean = false) {
    if (value == null || value == undefined) {
        return ''
    } else {
        value = value.toString()
        if (isInt) {
            value = value.replace(new RegExp(thousandSeparator, 'g'), '') // 删掉千分符
            value = value.replace('-', '') // 删掉负号
        }
        if (value.indexOf(decimalSeparator) > -1) {
            const [int, decimal] = value.split(decimalSeparator)
            return isInt ? int : decimal
        } else {
            return isInt ? value : ''
        }
    }
}

export function detail(props: InputNumberProps, state: InputNumberState, value: string | number, step: InputNumberProps['step'], type: 'add' | 'reduce') {
    let { precision, roundAt = 5, toNumber } = props
    const { decimalSeparator, thousandSeparator } = state
    let valueFloat = separate(decimalSeparator, thousandSeparator, value)
    let stepFloat = step !== undefined ? separate(decimalSeparator, thousandSeparator, step) : ''

    let ans: number | undefined | InstanceType<typeof PrecisionNumber>
    let stepFloatLength = stepFloat.toString().length
    let valueFloatLength = valueFloat.toString().length

    if (typeof precision === 'undefined') {
        precision = Math.max(stepFloatLength, valueFloatLength)
    }
    let coefficient = Math.pow(10, Math.abs(stepFloatLength - valueFloatLength))
    if (step !== undefined) {
        if (type === 'add') {
            ans = (+value * coefficient + step * coefficient) / coefficient
            if (!toNumber) ans = new PrecisionNumber(value).plus(step)
        } else {
            ans = (+value * coefficient - step * coefficient) / coefficient
            if (!toNumber) ans = new PrecisionNumber(value).minus(step)
        }
    }
    return roundAt === 5 ? (ans as number | InstanceType<typeof PrecisionNumber>).toFixed(precision) : customRound(ans, precision, roundAt)
}

/**
 * 判断是否是IE浏览器
 */
export function isIE() {
    if (window) {
        if (!!(window as any).ActiveXObject || 'ActiveXObject' in window) return true
    }
    return false
}

// 计算数量级
export function getMark(props: InputNumberProps, state: InputNumberState, value: string | number, local: any) {
    const {showMark, integerMarks = defaultIntegerMarks} = props
    const { decimalSeparator, thousandSeparator } = state;
    if (!showMark) return '' // 不显示数量级
    const integer = separate(decimalSeparator, thousandSeparator, value, true)
    const integerLength = Math.floor(Math.log10(+value4calc({value: integer}))) + 1 // 修复前置0时Mark错误问题
    if (integerLength < integerMarks[0].len) return '' // 低于最小位数，不显示
    if (integerLength > integerMarks[integerMarks.length - 1].len) return '' // 超出最大位数，不显示
    const currentMark = (integerMarks as IntegerMarksType).find(item => {
        return item.len === integerLength
    })
    if (!currentMark) return '' // 用户自定义不显示的数量级
    if ('mark' in currentMark && currentMark.mark !== undefined) {
        return currentMark.mark
    }
    return currentMark.key ? local.langMap[currentMark.key] : '' // 数量级
}

/**
     * @param value 符合数字格式的值
     */
export function getPrecision(props: InputNumberProps, value: InputNumberState['value'], precision: InputNumberProps['precision']) {
    const { roundAt = 5, decimalFormat } = props;
    if (value == null || value == undefined) return value
    if (!value && value === '') return value
    const {decimalSeparator} = getFormat(props)
    value = String(value)
    value = value.indexOf('e') !== -1 ? getFullNum({roundAt, value, decimalSeparator, precision}) : value
    if (precision == undefined || (value.indexOf('.') !== -1 && String(value.split('.')[1]).length === precision)) {
        return value
    }
    let before = value.substring(0, 1),
        len = value.length,
        after = value.substring(len - 1, len)
    before = before === '-' ? before : ''
    after = after === '-' ? after : ''
    // 是科学计数法，不replace -
    if (before) value = value.substring(1, len)
    if (after) value = value.substring(0, len - 1)
    let precV = '000000000000000000000000000000000000000000000000000000000000000000000000'
    if (value.indexOf('.') === -1) {
        precV = precV.substring(0, precision)
        precV = precV ? '.' + precV : precV
        if (!isNaN(+value) && (value.indexOf('-') != -1 || value.indexOf('+') != -1) && value.indexOf('e') != -1) {
            // 是科学计数法，不拼接0000000
        } else {
            value = value + precV
        }
    }
    let result = roundAt === 5 ? Number(value).toFixed(precision) : customRound(value, precision, roundAt)
    let tempValue = decimalFormat ? parseFloat(result) : result;
    return before + tempValue + after
}

/**
     * 校验value
     * @param {*} props
     * @param {原来的值} oldValue
     * @param {精度} precision
     */
export function judgeValue({
    isInit = false,
    props,
    state,
    oldValue,
    precision
}: {
        isInit?: boolean
        props: InputNumberProps
        state?: InputNumberState
        oldValue?: InputNumberState['value']
        precision?: InputNumberProps['precision']
    }): {
        value: string | number
        minusDisabled: boolean
        plusDisabled: boolean
    } {

    let currentValue
    let currentMinusDisabled = false
    let currentPlusDisabled = false
    let {value, min, max, defaultValue, displayCheckPrompt, locale, autoCorrectCase, invalidCaseError} = props
    const {minusRight} = getFormat(props)
    if (!value && value !== 0) {
        // undefined, null, 空字符串''，但不包括带空格的空字符串 '  '
        if (isInit && (defaultValue || defaultValue === 0)) {
            value = defaultValue
        } else {
            return {
                value: '',
                minusDisabled: false,
                plusDisabled: false
            }
        }
    }
    value = deleteSpaces(value)
    precision = precision ?? props.precision
    if (!max && max !== 0 && state) max = state.max
    if (!min && min !== 0 && state) min = state.min
    if (minusRight) {
        value = value?.toString() ?? ''
        if (value.indexOf('-') != -1) {
            // 所有位置的负号转到前边
            value = value.replace('-', '')
            value = '-' + value
        }
        value = value ? Number(value) : ''
    }
    if (value != undefined && value != null) {
        if (value === '') {
            currentValue = ''
            return {
                value: '',
                minusDisabled: false,
                plusDisabled: false
            }
        } else {
            // fix: 用户传入超长字符串数字时，类型转换会丢失精度，+"12345678901234.54663344"  ==> 12345678901234.547
            currentValue =
                    (typeof value === 'string' &&
                        (value.replace(/[^\d]/g, '').length >
                            16 /** 没超出安全数字，但长度超过16也会超出js数字可用的存储空间 */ ||
                            +value >= +max! /** 超出安全数字但没超过16位 */ ||
                            +value <= +min!)
                        ? value // 超长值不改变类型
                        : +value) ?? 0
        }
    } else {
        // NaN
        if (oldValue || oldValue === 0 || oldValue === '0') {
            currentValue = oldValue
        } else {
            // value为空
            return {
                value: '',
                minusDisabled: false,
                plusDisabled: false
            }
        }
    }
    if (currentValue == -Infinity) {
        return {
            value: min ?? '',
            minusDisabled: true,
            plusDisabled: false
        }
    }
    if (currentValue == Infinity) {
        return {
            value: max ?? '',
            minusDisabled: false,
            plusDisabled: true
        }
    }

    const local = getLangInfo(locale, i18n, 'inputNumber')
    if (min !== undefined && +currentValue <= min) {
        if (displayCheckPrompt && +currentValue < min) prompt(local.langMap.msgMin)
        currentMinusDisabled = true
        // QDJCJS-26550 针对传入超出范围的值后自动纠正功能，加开关，传属性后则不纠正，抛出报错事件
        autoCorrectCase && +currentValue < min ? invalidCaseError?.() : currentValue = min
    }
    if (max !== undefined && +currentValue >= max) {
        if (displayCheckPrompt && +currentValue > max) prompt(local.langMap.msgMax)
        currentPlusDisabled = true
        // QDJCJS-26550 针对传入超出范围的值后自动纠正功能，加开关，传属性后则不纠正，抛出报错事件
        autoCorrectCase && +currentValue > max ? invalidCaseError?.() : currentValue = max
    }

    if (precision !== undefined) {
        currentValue = getPrecision(props, currentValue, precision)
    }
    if (minusRight) {
        currentValue = '' + currentValue
        if (currentValue.indexOf('-') != -1) {
            // 负号转到后边
            currentValue = currentValue.replace('-', '')
            currentValue = currentValue + '-'
        }
    }
    return {
        value: currentValue,
        minusDisabled: currentMinusDisabled,
        plusDisabled: currentPlusDisabled
    }
}

/**
* @desc 超过最大值最小值情况处理
* @param v 输入值
*/
export function fixExceedNum(v: string | number, props: InputNumberProps, local: any, state: any):
    { value: string; showValue: string; placeholderShow: boolean } {
    let {max, min, displayCheckPrompt, round, precision, dir} = props
    const {thousandSeparator, decimalSeparator, minusRight} = state
    if (!max && max !== 0) max = state.max
    if (!min && min !== 0) min = state.min

    let value: number | string = numToFixed(decimalSeparator, v, precision, round)

    if (['-', decimalSeparator].includes(value)) { // 开始仅输入负号或小数点的场景
        return {
            value,
            showValue: value,
            placeholderShow: true
        }
    }
    if (minusRight || dir === "rtl") {
        if (value.indexOf('-') != -1) {
            // 所有位置的负号转到前边
            value = value.replace('-', '')
            value = '-' + value
        }
    }
    if (isNaN(parseFloat(value))) {
        // 空字符串等
        value = 0
    } else if (typeof value === 'string' && /\D/g.test(value[value.length - 1])) {
        // 末尾非数字
        value = parseFloat(value)
    } // 其他情况不做处理，原值下传
    if ((max || max === 0) && +value > +max) {
        if (displayCheckPrompt) prompt(local.langMap.msgMax)
        value = max
    }
    if ((min || min === 0) && +value < +min) {
        if (displayCheckPrompt) prompt(local.langMap.msgMin)
        value = min
    }
    if (precision !== undefined) {
        value = getPrecision(props, value, precision)
    }
    value = value.toString()
    return {
        value,
        showValue: value4show(value, thousandSeparator, decimalSeparator, minusRight),
        placeholderShow: true
    }
}

// 是否是千分位,支持自定义分隔符
export function isThousandth({ value, thousandSeparator, decimalSeparator }: { value: string, thousandSeparator: string, decimalSeparator: string }) {
    thousandSeparator = thousandSeparator === '.' ? '\\.' : thousandSeparator
    decimalSeparator = decimalSeparator === '.' ? '\\.' : decimalSeparator
    // TODO：千分位为空场景正则处理
    if (!thousandSeparator) return false
    const regex = new RegExp(`(?:^[-]?[1-9]\\d{0,2}(?:$|(?:${thousandSeparator}\\d{3})*(?:$|(${decimalSeparator}\\d{1,2}$))))|(?:(?:^[0](${decimalSeparator}\\d{1,2})?)|(?:^[-][0]${decimalSeparator}\\d{1,2}))$`)
    return regex.test(value)
}

export function isEqual(state: InputNumberState) {
    // 如果当前数据不合法，之前合法数据替换当前数据
    let {preValue, value} = state
    if (value !== preValue && preValue !== undefined && preValue !== '') {
        value = preValue
    }
    return value
}

// 字符串且有减号，可调整减号位置，数据类型保持不变
export function changeMinusRightPosition(value: string | number, minusRight: boolean): string | number {
    if (minusRight) {
        if (typeof value === 'string' && value.indexOf('-') !== -1) {
            value = '-' + value.replace('-', '')
        } else if (typeof value === 'number' && value < 0) {
            value = '-' + String(value).replace('-', '')
        }
    }
    return value
}